import React, { useState, useRef, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAuth as useAdminAuth } from "../context/AdminContext";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const { pathname } = useLocation();
  const { user, logout, login, loading } = useAuth();
  const { user: adminUser, logout: adminLogout, isAuthenticated: isAdminAuth } = useAdminAuth();
  const dropdownRef = useRef(null);

  const mobileMenuRef = useRef(null);

  // Close dropdown when clicking/tapping outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Don't close if interacting inside the desktop dropdown area
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
      // Don't close if interacting inside the mobile menu
      if (mobileMenuRef.current && mobileMenuRef.current.contains(e.target)) return;
      setOpenDropdown(null);
    };
    // Use click (fires after touchend) instead of mousedown/touchstart
    // so that link navigation inside dropdowns fires before the dropdown closes
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  if (loading) {
    return (
      <nav className="bg-[#135879] px-4 text-white h-[50px] relative z-[50] border-b border-[#d4af37] flex items-center justify-end">
        <div className="animate-pulse">Loading...</div>
      </nav>
    );
  }

  const toggleMenu = () => setIsOpen(!isOpen);

  const menuItems = [
    { label: "Business Directory", path: "/business-directory" },
    { label: "Manage Business", path: "/submit-business" },
    { label: "About Us", path: "/about" },
    { label: "Events", path: "/events" },
    { label: "Advisory", path: "/advisory" },
    {
      label: "Resources",
      children: [
        { label: "Newsletters", path: "/resources/newsletters" },
        { label: "Blogs", path: "/resources/blogs" },
        { label: "Videos", path: "/resources/videos" },
        { label: "Documents & Forms", path: "/resources/guides" },
        { label: "Announcements", path: "/resources/announcements" },
      ],
    },
    { label: "Contact Us", path: "/contact" },
  ];

  const getLinkClasses = (path) =>
    `transition cursor-pointer px-2 h-full block flex items-center justify-center py-1 ${
      pathname === path
        ? "bg-[#d4af37] text-black font-semibold"
        : "hover:text-[#d4af37]"
    }`;

  const isResourceActive = pathname.startsWith("/resources");

  const handleLogout = () => {
    if (isAdminAuth) {
      adminLogout();
    } else {
      logout();
    }
  };

  return (
    <>
      <header className="bg-[#135879] relative px-4 text-white h-[60px] md:h-[50px] z-[50] border-b border-[#d4af37]">
        <div className="flex items-center justify-between md:justify-end h-full">
          <Link to="/" className="md:absolute md:top-4 md:left-[5%]">
            <img
              src="/logo.jpg"
              alt="Logo"
              className="h-[50px] w-[50px] md:h-[90px] md:w-[90px] border-2 md:border-4 border-[#d4af37] p-1 bg-white rounded-full md:rounded-none"
            />
          </Link>
          <button
            onClick={toggleMenu}
            className="md:hidden block text-white focus:outline-none p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <ul className="hidden md:flex h-full gap-6 text-sm font-medium" ref={dropdownRef}>
            {menuItems.map((item) => (
              <li className="h-full relative" key={item.label}>
                {item.children ? (
                  /* Dropdown menu item */
                  <>
                    <button
                      onClick={() =>
                        setOpenDropdown(openDropdown === item.label ? null : item.label)
                      }
                      className={`transition cursor-pointer px-2 h-full flex items-center justify-center py-1 gap-1 ${
                        isResourceActive
                          ? "bg-[#d4af37] text-black font-semibold"
                          : "hover:text-[#d4af37]"
                      }`}
                    >
                      {item.label}
                      <ChevronDown
                        size={14}
                        className={`transition-transform duration-200 ${
                          openDropdown === item.label ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {openDropdown === item.label && (
                      <div className="absolute top-full left-0 bg-[#135879] border border-[#d4af37]/30 rounded-b-lg shadow-xl min-w-[180px] py-1 z-50">
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.path}
                            onClick={() => setOpenDropdown(null)}
                            className={`block px-4 py-2.5 text-sm transition-colors ${
                              pathname === child.path
                                ? "bg-[#d4af37] text-black font-semibold"
                                : "text-white hover:bg-white/10 hover:text-[#d4af37]"
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  /* Regular menu item */
                  <Link to={item.path} className={getLinkClasses(item.path)}>
                    {item.label}
                  </Link>
                )}
              </li>
            ))}
            {(user || (isAdminAuth && adminUser)) ? (
              <div className="flex items-center gap-4 ml-4">
                <span className="text-white font-medium">
                  {isAdminAuth ? (adminUser?.username || "Admin") : (user?.name || user?.email || "username")}
                </span>
                <button
                  onClick={handleLogout}
                  className="bg-[#d4af37] hover:bg-[#c19b2e] text-black font-semibold px-4 py-2 rounded transition-colors duration-200"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="flex items-center">
                <button
                  onClick={() => login()}
                  className="bg-[#d4af37] hover:bg-[#c19b2e] text-black font-semibold px-4 py-2 rounded transition-colors duration-200"
                >
                  Login
                </button>
              </div>
            )}
          </ul>
        </div>
        {isOpen && (
          <div ref={mobileMenuRef} className="absolute top-full left-0 right-0 bg-[#135879] shadow-xl border-t border-[#d4af37]/30 md:hidden">
            <div className="max-h-[calc(100vh-100px)] overflow-y-auto">
              {/* Navigation Links */}
              <div className="px-4 py-6">
                <nav className="space-y-4">
                  {menuItems.map((item) =>
                    item.children ? (
                      /* Mobile dropdown */
                      <div key={item.label}>
                        <button
                          onClick={() =>
                            setOpenDropdown(openDropdown === item.label ? null : item.label)
                          }
                          className={`flex items-center justify-between w-full text-left px-4 py-3 rounded-lg text-base sm:text-lg font-medium transition-all duration-200 ${
                            isResourceActive
                              ? "bg-[#d4af37] text-black"
                              : "text-white hover:bg-white/10 hover:text-[#d4af37]"
                          }`}
                        >
                          {item.label}
                          <ChevronDown
                            size={18}
                            className={`transition-transform duration-200 ${
                              openDropdown === item.label ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        {openDropdown === item.label && (
                          <div className="ml-4 mt-2 space-y-1 border-l-2 border-[#d4af37]/30 pl-4">
                            {item.children.map((child) => (
                              <Link
                                key={child.label}
                                to={child.path}
                                onClick={() => {
                                  setIsOpen(false);
                                  setOpenDropdown(null);
                                }}
                                className={`block px-4 py-2.5 rounded-lg text-base font-medium transition-all duration-200 ${
                                  pathname === child.path
                                    ? "bg-[#d4af37] text-black"
                                    : "text-white hover:bg-white/10 hover:text-[#d4af37]"
                                }`}
                              >
                                {child.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Mobile regular item */
                      <Link
                        key={item.label}
                        to={item.path}
                        onClick={() => setIsOpen(false)}
                        className={`block w-full text-left px-4 py-3 rounded-lg text-base sm:text-lg font-medium transition-all duration-200 ${
                          pathname === item.path
                            ? "bg-[#d4af37] text-black"
                            : "text-white hover:bg-white/10 hover:text-[#d4af37]"
                        }`}
                      >
                        {item.label}
                      </Link>
                    )
                  )}
                </nav>
              </div>

              {/* Mobile Auth Section */}
              <div className="border-t border-[#d4af37]/30 px-4 py-6 bg-[#0e4562]">
                {(user || (isAdminAuth && adminUser)) ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="text-white font-medium text-sm">
                          Welcome back!
                        </p>
                        <p className="text-[#d4af37] text-sm">
                          {isAdminAuth ? (adminUser?.username || "Admin") : (user?.name || user?.email || "user")}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsOpen(false);
                      }}
                      className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 text-lg"
                    >
                      Logout
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-white/80 text-center text-sm">
                      Sign in to access your account
                    </p>
                    <button
                      onClick={() => {
                        login();
                        setIsOpen(false);
                      }}
                      className="w-full bg-[#d4af37] hover:bg-[#c19b2e] text-black font-semibold py-3 px-4 rounded-lg transition-colors duration-200 text-lg"
                    >
                      Login
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Navbar;
